const TERMINATOR = 0x80;
const MAX_BYTE = 0xff;

function parseKey(key: string): number[] | null {
  if (key.length === 0 || key.length % 2 !== 0) return null;
  if (!/^[0-9a-f]+$/.test(key)) return null;
  const bytes: number[] = [];
  for (let i = 0; i < key.length; i += 2) {
    bytes.push(Number.parseInt(key.slice(i, i + 2), 16));
  }
  return bytes;
}

function encodeKey(bytes: number[]): string {
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The ordering key for a surface appended after `last`; `null` for the first
 * surface in a document. Returns `null` when `last` is unparseable or already at
 * the ceiling, and callers must treat that as "cannot append" rather than
 * substituting a key.
 *
 * There is deliberately no `keyBetween` here. The server mints between-keys with
 * `fractional_index` 2.0.2, whose `new_between` subtracts from a byte without
 * checking for zero (fract_index.rs:201,207) — it panics in debug and returns
 * keys outside the requested bounds in release. Reproducing that faithfully in
 * TypeScript, which has neither panics nor u8 wrapping, risks the client and
 * server disagreeing about order inside a CRDT. Reordering swaps two existing
 * keys instead; see swapOrder.
 */
export function keyAfter(last: string | null): string | null {
  if (last === null) return encodeKey([TERMINATOR]);
  const bytes = parseKey(last);
  if (bytes === null) return null;
  if (bytes[0] >= MAX_BYTE) return null;
  return encodeKey([bytes[0] + 1, TERMINATOR]);
}

/** The ordering fields of one manifest entry — the subset reorder needs. */
export interface OrderedEntry {
  id: string;
  order: string;
}

/** The two writes that perform a reorder. Both entries already existed. */
export interface OrderSwap {
  moved: OrderedEntry;
  displaced: OrderedEntry;
}

/**
 * The two `order` writes that move `id` one position in `direction`, or `null`
 * when there is nowhere to move — already at that end, or not in the list.
 * Callers treat `null` as a no-op.
 *
 * Reordering exchanges two keys that already exist rather than minting one
 * between them; see the note on `keyAfter` for why the client does not
 * subdivide. Entries are ranked by `order` with an `id` tie-break, matching how
 * the manifest itself sorts (design §4.3), so the caller may pass them in any
 * order.
 */
export function swapOrder(
  entries: readonly OrderedEntry[],
  id: string,
  direction: 'up' | 'down',
): OrderSwap | null {
  const ranked = [...entries].sort((a, b) => (a.order === b.order
    ? (a.id < b.id ? -1 : 1)
    : a.order < b.order ? -1 : 1));

  const index = ranked.findIndex((e) => e.id === id);
  if (index === -1) return null;

  const neighbourIndex = direction === 'up' ? index - 1 : index + 1;
  if (neighbourIndex < 0 || neighbourIndex >= ranked.length) return null;

  const entry = ranked[index];
  const neighbour = ranked[neighbourIndex];
  return {
    moved: { id: entry.id, order: neighbour.order },
    displaced: { id: neighbour.id, order: entry.order },
  };
}
